import { Map, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import whatsappLineIcon from '@/assets/clients/actions/whatsapp-line.svg'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { useQuickActions } from '@/hooks/useQuickActions'
import { cn } from '@/lib/utils'
import { technicianMapDestination, type TechnicianLane } from './grid-helpers'

interface TechnicianQuickActionsProps {
  lane: TechnicianLane
}

const actionButtonClass =
  'flex h-6 w-6 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] text-[#272c36] transition-colors disabled:cursor-not-allowed disabled:opacity-45'
const orangeActionButtonClass = cn(
  actionButtonClass,
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
)
const whatsappActionButtonClass = cn(
  actionButtonClass,
  'group/whatsapp hover:border-[#10b981] hover:bg-[#ecfdf5] hover:text-[#10b981]'
)

export function TechnicianQuickActions({ lane }: TechnicianQuickActionsProps) {
  const { t } = useTranslation('jobs')
  const { callClient, navigateToAddress, whatsappClient } = useQuickActions()
  const technician = lane.technician
  const mapDestination = technicianMapDestination(technician)
  const hasPhone = Boolean(technician.phone)
  const hasAddress = Boolean(
    mapDestination.address ||
    (typeof mapDestination.latitude === 'number' &&
      typeof mapDestination.longitude === 'number')
  )
  const callLabel = t('agenda.actions.callTechnician', { name: lane.name })
  const whatsappLabel = t('agenda.actions.whatsappTechnician')
  const mapLabel = t('agenda.actions.mapTechnician', { name: lane.name })

  return (
    <span
      className='flex shrink-0 items-center gap-1'
      onDragStart={event => event.stopPropagation()}
    >
      <TruncatedTextTooltip
        content={callLabel}
        delayDuration={0}
        alwaysShow
        closeOnPointerDown
        className='inline-flex shrink-0'
      >
        <button
          type='button'
          aria-label={callLabel}
          draggable={false}
          disabled={!hasPhone}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => {
            event.stopPropagation()
            callClient(technician.phone)
          }}
          className={orangeActionButtonClass}
        >
          <Phone className='h-3.5 w-3.5' />
        </button>
      </TruncatedTextTooltip>

      <TruncatedTextTooltip
        content={whatsappLabel}
        delayDuration={0}
        alwaysShow
        closeOnPointerDown
        className='inline-flex shrink-0'
      >
        <button
          type='button'
          aria-label={whatsappLabel}
          draggable={false}
          disabled={!hasPhone}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => {
            event.stopPropagation()
            whatsappClient(technician.phone)
          }}
          className={whatsappActionButtonClass}
        >
          <img
            src={whatsappLineIcon}
            alt=''
            className='size-3.5 shrink-0 transition-[filter] group-hover/whatsapp:[filter:brightness(0)_saturate(100%)_invert(55%)_sepia(74%)_saturate(1027%)_hue-rotate(118deg)_brightness(94%)_contrast(87%)]'
          />
        </button>
      </TruncatedTextTooltip>

      <TruncatedTextTooltip
        content={mapLabel}
        delayDuration={0}
        alwaysShow
        closeOnPointerDown
        className='inline-flex shrink-0'
      >
        <button
          type='button'
          aria-label={mapLabel}
          draggable={false}
          disabled={!hasAddress}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => {
            event.stopPropagation()
            navigateToAddress(mapDestination)
          }}
          className={orangeActionButtonClass}
        >
          <Map className='h-3.5 w-3.5' />
        </button>
      </TruncatedTextTooltip>
    </span>
  )
}
